import { message, notification } from 'ant-design-vue'
import { getRoot } from '/@/root'
import { PostFlightAreaBody, saveFlightArea } from '/@/api/flight-area'
import { generatePolyContent } from '/@/utils/map-layer-utils'
import { GeojsonCoordinate } from '/@/utils/genjson'
import { uuidv4 } from '/@/utils/uuid'
import { CommonHostWs } from '/@/websocket'
import { FlightAreasDroneLocation } from '/@/types/flight-area'
import rootStore from '/@/store'
import { h } from 'vue'
import { useGMapCover } from '/@/hooks/use-g-map-cover'
import moment from 'moment'
import { DATE_FORMAT } from '/@/utils/constants'

export function useFlightArea () {
  const root = getRoot()
  const store = rootStore
  const coverMap = store.state.coverMap

  let useGMapCoverHook = useGMapCover()

  // Bornes des géofences DJI. MIN_RADIUS = borne basse côté SDK (FeatureProperty @Min(10)).
  // MAX_RADIUS / MAX_VERTICES sont des garde-fous prudents — à confirmer dans la doc DJI
  // selon le firmware (Mavic 3E). Mieux vaut bloquer avant le sync qu'au niveau du drone.
  const MIN_RADIUS = 10
  const MAX_RADIUS = 5000
  const MAX_VERTICES = 100

  function isCircleObj (obj: any): boolean {
    return typeof obj.getRadius === 'function' && obj.getRadius() > 0
  }

  // Valide la géométrie avant envoi : rayon pour un cercle, nombre de sommets pour un polygone.
  function checkGeometry (obj: any): boolean {
    if (isCircleObj(obj)) {
      const r = obj.getRadius()
      if (r < MIN_RADIUS) { message.error(`Le rayon doit être d'au moins ${MIN_RADIUS} m.`); obj.remove(); return false }
      if (r > MAX_RADIUS) { message.error(`Le rayon ne peut pas dépasser ${MAX_RADIUS} m.`); obj.remove(); return false }
      return true
    }
    const path = obj.getPath()
    if (path.length < 3) { message.error('Le polygone doit avoir au moins 3 points.'); obj.remove(); return false }
    if (path.length > MAX_VERTICES) { message.error(`Le polygone ne peut pas dépasser ${MAX_VERTICES} sommets.`); obj.remove(); return false }
    return true
  }

  function setExtData (obj: any) {
    let ext = obj.getExtData()
    const id = uuidv4()
    const name = `${ext.type}-${moment().format(DATE_FORMAT)}`
    ext = Object.assign({}, ext, { id, name })
    obj.setExtData(ext)
    return ext
  }

  function createFlightArea (obj: any) {
    const ext = obj.getExtData()
    const data = { id: ext.id, type: ext.type, name: ext.name }

    const path = obj.getPath()
    const content = generatePolyContent(path).content
    const coordinates: GeojsonCoordinate[][] = [content.geometry.coordinates[0] as GeojsonCoordinate[]]
    content.geometry.coordinates = coordinates

    saveFlightArea(Object.assign({}, data, { content }) as PostFlightAreaBody).then(res => {
      if (res.code !== 0) {
        message.error(res.message || 'Échec de la création de la zone de vol.')
        useGMapCoverHook.removeCoverFromMap(ext.id)
      } else {
        message.success('Zone de vol créée.')
      }
    }).catch(() => {
      message.error('Erreur réseau lors de la création de la zone de vol.')
      useGMapCoverHook.removeCoverFromMap(ext.id)
    }).finally(() => obj.remove())
  }

  function getDrawFlightAreaCallback (obj: any) {
    useGMapCoverHook = useGMapCover()
    const ext = setExtData(obj)

    if (!checkGeometry(obj)) return
    createFlightArea(obj)
  }

  // With Mapbox (WGS-84 native), no coordinate conversion needed
  const getWgs84 = <T extends GeojsonCoordinate | GeojsonCoordinate[]>(coordinate: T): T => coordinate
  const getGcj02 = <T extends GeojsonCoordinate | GeojsonCoordinate[]>(coordinate: T): T => coordinate

  const onFlightAreaDroneLocationWs = (data: CommonHostWs<FlightAreasDroneLocation>) => {
    const nearArea = data.host.drone_locations.filter(val => !val.is_in_area)
    const inArea = data.host.drone_locations.filter(val => val.is_in_area)
    notification.warning({
      key: `flight-area-${data.sn}`,
      message: `Drone(${data.sn}) flight area information`,
      description: h('div',
        [
          h('div', [
            h('span', { class: 'fz18' }, 'In the flight area: '),
            h('ul', [
              ...inArea.map(val => h('li', `There are ${val.area_distance} meters from the edge of the area(${coverMap[val.area_id]?.[1]?.getText?.() || val.area_id}).`))
            ])
          ]),
          h('div', [
            h('span', { class: 'fz18' }, 'Near the flight area: '),
            h('ul', [
              ...nearArea.map(val => h('li', `There are ${val.area_distance} meters from the edge of the area(${coverMap[val.area_id]?.[1]?.getText?.() || val.area_id}).`))
            ])
          ])
        ]),
      duration: null,
      style: { width: '420px', marginTop: '-8px', marginLeft: '-28px' },
    })
  }

  return {
    getDrawFlightAreaCallback,
    getGcj02,
    getWgs84,
    onFlightAreaDroneLocationWs,
  }
}
